import React from 'react';
import { Input } from '@chakra-ui/react';
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from 'use-places-autocomplete';

export const PlacesAutocomplete = ({
  value,
  onChange,
  placeholder = "Enter a location",
  isRequired = false,
  ...inputProps
}) => {
  const [justSelected, setJustSelected] = React.useState(false);

  const {
    ready,
    value: searchValue,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      /* Define search scope here if needed */
    },
    debounce: 300,
  });

  const handleInput = (e) => {
    setJustSelected(false);
    setValue(e.target.value);
  };

  const handleFocus = () => {
    if (searchValue && searchValue.trim()) {
      // Re-trigger search with current value to show suggestions
      setValue(searchValue, true);
    }
  };

  const handleSelect = async (description) => {
    setJustSelected(true);
    setValue(description, false);
    clearSuggestions();

    try {
      const results = await getGeocode({ address: description });
      const { lat, lng } = await getLatLng(results[0]);

      // Call the parent's onChange with the selected location
      onChange({
        address: description,
        lat,
        lng,
        placeId: results[0].place_id
      });
    } catch (error) {
      console.error('Error getting geocode:', error);
      // Fallback to just the address string
      onChange(description);
    }
  };

  React.useEffect(() => {
    if (justSelected) {
      // Don't update the input value if we just selected something
      return;
    }

    if (value && typeof value === 'string') {
      setValue(value);
    } else if (value && value.address) {
      setValue(value.address);
    }
  }, [value, setValue, justSelected]);

  return (
    <div style={{ position: 'relative' }}>
      <Input
        value={searchValue}
        onChange={handleInput}
        onFocus={handleFocus}
        disabled={!ready}
        placeholder={placeholder}
        isRequired={isRequired}
        {...inputProps}
      />

      {status === 'OK' && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'var(--chakra-colors-dark-700)',
            border: '1px solid var(--chakra-colors-dark-600)',
            borderTop: 'none',
            borderRadius: '0 0 6px 6px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            zIndex: 1000,
            maxHeight: '200px',
            overflowY: 'auto'
          }}
        >
          {data.map((suggestion) => {
            const {
              place_id,
              structured_formatting: { main_text, secondary_text },
            } = suggestion;

            return (
              <div
                key={place_id}
                onClick={() => handleSelect(suggestion.description)}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--chakra-colors-dark-600)',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--chakra-colors-dark-600)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <div style={{ fontWeight: '500', color: 'var(--chakra-colors-gray-100)' }}>
                  {main_text}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--chakra-colors-gray-400)' }}>
                  {secondary_text}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
